/**
 * Wrap codes pie chart from call history (Recharts + hover tooltip).
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { buildCallHistoryParams, WRAP_CHART_COLORS, type WidgetFilterConfig } from '../../lib/widgetScope';

type WrapItem = { label: string; count: number };

interface WidgetCallWrapPieProps {
  config?: WidgetFilterConfig;
}

export default function WidgetCallWrapPie({ config }: WidgetCallWrapPieProps) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<WrapItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = buildCallHistoryParams(config);
      setLoading(true);
      try {
        const res = await fetch(`${base}/api/calls/wrap-breakdown?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError((data as { error?: string }).error || `HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        setItems(data.items ?? []);
        setTotal(data.total ?? 0);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, config?.scope, config?.campaign_id, config?.ipscape_agent_id, config?.date_range]);

  const chartData = useMemo(
    () =>
      items.slice(0, 10).map((item, i) => ({
        name: item.label,
        value: item.count,
        fill: WRAP_CHART_COLORS[i % WRAP_CHART_COLORS.length],
      })),
    [items]
  );

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.35rem 0.5rem',
        minHeight: 120,
      }}
    >
      {loading && <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>{error}</p>}
      {!loading && !error && total === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>No calls in range</p>
      )}
      {!loading && !error && total > 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: 0 }}>
          <div style={{ flex: '1 1 58%', height: '100%', minWidth: 0, minHeight: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="88%"
                  innerRadius="0%"
                  stroke="var(--widget-bg)"
                  strokeWidth={1}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'var(--widget-bg)',
                    border: '1px solid var(--header-border)',
                    borderRadius: 6,
                    fontSize: '0.75rem',
                  }}
                  formatter={(value, _name, item) => {
                    const count = Number(value) || 0;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const label = (item?.payload as { name?: string })?.name ?? String(_name);
                    return [`${count} (${pct}%)`, label];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul
            style={{
              flex: '0 1 42%',
              margin: 0,
              padding: 0,
              listStyle: 'none',
              overflow: 'auto',
              fontSize: '0.6rem',
              lineHeight: 1.35,
              color: 'var(--text-muted)',
              maxHeight: '100%',
            }}
          >
            {chartData.map((s, i) => (
              <li
                key={s.name}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.3rem',
                  marginBottom: '0.2rem',
                  opacity: activeIndex === undefined || activeIndex === i ? 1 : 0.45,
                }}
                title={`${s.name}: ${s.value}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(undefined)}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 1,
                    background: s.fill,
                    flexShrink: 0,
                    marginTop: 3,
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                  }}
                >
                  {s.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
